import { Client } from '@stomp/stompjs';
import { WSS_PATH } from "config";

import { 
    setSubscribedCreativeTopic, 
    setCreativeProgress, 
    setCreativeFailedGeneration,
    setSubscribedTextTopic, 
    setTextProgress,
    setTextFailedGeneration
} from "./slices/campaign";

import { 
    setSubscribedOrderTopic, 
    setOrderStatus
} from "./slices/workspace";

import {
    setSubscribedCreatorCreativeTopic,
    setCreatorResultProgress,
    setCreatorResultFailedGeneration,
} from "./slices/smartCreatorEdit";

export const stompMiddleware = ({ dispatch }) => {
    const client = new Client({
        brokerURL: WSS_PATH,
        connectHeaders: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        },
        debug: function (str) {
            console.log(str);
        },
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        reconnectDelay: 2000,
    });

    client.onStompError = function (frame) {
        // Will be invoked in case of error encountered at Broker
        console.error('Broker reported error: ' + frame.headers['message']);
        console.error('Additional details: ' + frame.body);
    };

    client.onWebSocketError = function (evt) {
        // Network / low-level websocket issue: avoid throwing and just log clearly
        console.error('STOMP WebSocket error', evt);
    };

    client.onDisconnect = function () {
        console.debug('STOMP disconnected');
    };

    const isClientUsable = () => client && client.active && client.connected;

    const handleStatusPayload = (gentype, body) => {
        if (!body) {
            console.log('got empty message');
            return;
        }

        if (gentype === 'text') {
            if (body.includes('Generation Failed')) {
                dispatch(setTextFailedGeneration(body));
            } else {
                dispatch(setTextProgress(Number(body)));
            }
        } else if (gentype === 'image') {
            if (body.includes('Generation Failed')) {
                dispatch(setCreativeFailedGeneration(body));
            } else {
                dispatch(setCreativeProgress(Number(body)));
            }
        } else if (gentype === 'ai-image') {
            if (body.includes('Generation Failed')) {
                dispatch(setCreatorResultFailedGeneration(body));
            } else {
                dispatch(setCreatorResultProgress(Number(body)));
            }
        } else {
            dispatch(setOrderStatus(body));
        }
    };

    const markSubscribed = (gentype) => {
        if (gentype === 'text') {
            dispatch(setSubscribedTextTopic(true));
        } else if (gentype === 'image') {
            dispatch(setSubscribedCreativeTopic(true));
        } else if (gentype === 'ai-image') {
            dispatch(setSubscribedCreatorCreativeTopic(true));
        } else {
            dispatch(setSubscribedOrderTopic(true));
        }
    };

    const safePublishStatus = (gentype, id) => {
        if (isClientUsable()) {
            client.publish({
                destination: `/app/${gentype}.status`,
                body: id
            });
        } else {
            console.error('Cannot publish STOMP message: there is no underlying STOMP connection.');
        }
    };

    const subscribeToStatus = (gentype, id) => {
        if (!isClientUsable()) {
            console.error('Cannot subscribe to STOMP topic: there is no underlying STOMP connection.');
            return;
        }

        client.subscribe(
            `/user/topic/${gentype}.status`,
            (payload) => handleStatusPayload(gentype, payload.body),
            { id: `${gentype}_${id}` }
        );
    };

    const handleConnected = (gentype, id) => {
        subscribeToStatus(gentype, id);
        markSubscribed(gentype);
        safePublishStatus(gentype, id);
    };

    return next => action => {
        switch (action.type) {
        case 'connect':
            if (!client.connected) {
                client.onConnect = function () {
                    handleConnected(action.gentype, action.id);
                };

                client.activate();
            } else {
                handleConnected(action.gentype, action.id);
            }
            break;
        case 'unsubscribe':
            if (isClientUsable()) {
                try {
                    client.unsubscribe(`${action.gentype}_${action.id}`)
                } catch (e) {
                    console.error('Failed to unsubscribe STOMP topic: there is no underlying STOMP connection.', e);
                }
            } else {
                console.warn('Skip STOMP unsubscribe: there is no underlying STOMP connection.');
            }
            if(action.gentype === 'text') {
                dispatch(setSubscribedTextTopic(false))
                dispatch(setTextProgress(null))
            } else if(action.gentype === 'image') {
                dispatch(setSubscribedCreativeTopic(false))
                dispatch(setCreativeProgress(null))
            } else {
                dispatch(setSubscribedOrderTopic(false))
                dispatch(setOrderStatus(null))
            }
            break;
        case 'disconnect':
            dispatch(setSubscribedTextTopic(false))
            dispatch(setSubscribedCreativeTopic(false))
            dispatch(setSubscribedOrderTopic(false))
            if (client && client.active) {
                client.deactivate();
            }
            break;
        default:
            return next(action);
        }
    }
};